import React, { useContext, useEffect, useState } from "react";
import styled, { css, keyframes } from "styled-components";
import { useSelector } from "react-redux";
import CartProduct from "./CartProduct";
import {
  DEFAULT_CURRENCY,
  freeShippingThreshold,
  getShippingCostPrice,
  qualifiesForFreeShipping,
} from "../../../utils/global_const";
import { ProductContext } from "../../../context/ProductContext";
import { useNavigate } from "react-router-dom";
import { useNavigation } from "../../../utils/navigation";
import { useTranslation } from "react-i18next";

const slideIn = keyframes`
  from {
    transform: translateX(100%);
  }

  to {
    transform: translateX(0);
  }
`;

const slideOut = keyframes`
  from {
    transform: translateX(0);
  }

  to {
    transform: translateX(100%);
  }
`;
const slideInD = keyframes`
  from {
    transform: translateY(100%);
    opacity: 0;
  }

  to {
    transform: translateY(0);
    opacity: 1;
  }
`;

const slideOutD = keyframes`
  from {
    transform: translateY(0);
    opacity: 1;
  }

  to {
    transform: translateY(100%);
    opacity: 0;
  }
`;
const ContainerX = styled.div`
  position: relative;
`;
const Container = styled.div`
  margin-top: var(--spacing-xxs);
  background-color: rgba(0, 0, 0, 0.35);
  position: fixed;
  right: 0;
  top: 0;
  z-index: var(--zindex-default);
  min-width: 100%;
  height: 100vh;

  box-sizing: border-box;
  overflow-x: hidden;
  opacity: ${(props) => (props.$isOpen ? 1 : 0)};
  transition: opacity 400ms ease-in-out;
  visibility: ${(props) => (props.$shouldBeVisible ? "visible" : "hidden")};

  @media (min-width: 768px) {
    background-color: transparent;
  }
`;
const Wrapper = styled.div`
  z-index: calc(var(--zindex-default) + 1);
  background-color: var(--bg-100);

  width: 100%;
  height: 100%;
  min-height: 100%;
  position: fixed;
  right: 0;
  top: 0;
  transform: translateX(100%);
  transition: transform 0.3s ease-in-out;
  visibility: ${(props) => (props.$shouldBeVisible ? "visible" : "hidden")};
  display: flex;
  flex-direction: column;
  ${({ $isOpen }) =>
    $isOpen
      ? css`
          animation: ${slideIn} 0.4s ease-out forwards; // Duže trajanje za slide-in
        `
      : css`
          animation: ${slideOut} 0.1s ease-in forwards; // Kraće trajanje za slide-out
        `}
  @media (min-width: 768px) {
    position: absolute;
    right: 0%;
    top: calc(100% + 10px);
    width: 420px;
    height: auto;
    min-height: auto;
    max-height: 82vh;
    border-radius: var(--border-radius-large);
    overflow: hidden;
    box-shadow: var(--shadow-large);
    ${({ $isOpen }) =>
      $isOpen
        ? css`
            animation: ${slideInD} 0.3s ease-out forwards; // Duže trajanje za slide-in
          `
        : css`
            animation: ${slideOutD} 0.1s ease-in forwards; // Kraće trajanje za slide-out
          `};
  }
`;

const CartContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  cursor: pointer;
  height: 36px;
  min-width: 36px;
  min-height: 36px;
  @media (min-width: 768px) {
    height: 40px;
    min-width: 40px;
    min-height: 40px;
  }
  svg {
    fill: var(--text-100);
    transition: fill 0.4s ease; /* Dodaj tranziciju za glatki prelaz boje */
  }

  &:hover svg {
    fill: var(--text-200); /* Boja pri hoveru */
  }
  &:hover h4 {
    color: var(--text-200);
  }
`;
const CartText = styled.h4`
  display: none;
  cursor: pointer;
  @media (min-width: 768px) {
    display: inline-block;
  }

  font-weight: 200;
  color: var(--text-100);
`;
const SumQuantity = styled.div`
  cursor: pointer;
  position: absolute;
  right: -10px;
  top: -4px;

  background-color: var(--primary-200);
  color: var(--bg-100);
  border-radius: var(--border-radius-large);
  padding: 4px 2px;
  margin: 0;
  min-width: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: var(--font-size-small);
  font-weight: 200;
`;

const XDiv = styled.div`
  flex-shrink: 0;
  padding: 0 var(--spacing-md);
  cursor: pointer;
  height: var(--navbar-height);
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: var(--bg-100);
  color: var(--text-200);
  border-bottom: 1px solid var(--bg-300);
`;

const CloseButton = styled.div`
  width: 34px;
  height: 34px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.15s ease;

  &:hover {
    background-color: var(--bg-200);
  }
`;

const ShippingBar = styled.div`
  flex-shrink: 0;
  padding: var(--spacing-sm) var(--spacing-md);
  background-color: var(--primary-300);
`;

const ShippingText = styled.p`
  margin: 0 0 6px 0;
  font-size: 0.72rem;
  font-weight: 500;
  color: var(--primary-100);
  display: flex;
  align-items: center;
  gap: 6px;
`;

const ProgressTrack = styled.div`
  width: 100%;
  height: 6px;
  border-radius: var(--border-radius-large);
  background-color: var(--bg-100);
  overflow: hidden;
`;

const ProgressFill = styled.div`
  height: 100%;
  border-radius: var(--border-radius-large);
  background-color: var(--primary-100);
  width: ${(props) => props.$percent}%;
  transition: width 0.4s ease;
`;

const MiddleDiv = styled.div`
  flex: 1;
  padding: var(--spacing-md) 0 var(--spacing-md);
  overflow: auto; /* omogućava skrolanje unutar div-a */
  -webkit-overflow-scrolling: touch;
  touch-action: pan-y;
  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-thumb {
    background-color: var(--bg-300);
    border-radius: 5px;
  }
`;

const ItemsInner = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const Logo = styled.div`
  color: var(--text-100);

  font-size: var(--font-size-large);
  font-style: normal;
  font-weight: 500;
  letter-spacing: -0.438px;
  display: flex;
  gap: var(--spacing-xs);

  align-items: center;
  cursor: pointer;
`;
const IconDiv = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Bottom = styled.div`
  flex-shrink: 0;
  width: 100%;
  border-top: 1px solid var(--bg-300);
  box-shadow: 0 -4px 8px rgba(0, 0, 0, 0.08);
  background-color: var(--bg-200);
`;

const BottomWrapper = styled.div`
  width: 100%;
  padding: var(--spacing-sm) var(--spacing-md) var(--spacing-md);
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;
const FlexDiv = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const SavingsRow = styled(FlexDiv)`
  color: var(--success-color);
  font-size: var(--font-size-small);
  font-weight: 500;
`;

const SummaryRow = styled(FlexDiv)`
  font-size: var(--font-size-small);
  color: var(--text-200);
`;

const TotalRow = styled(FlexDiv)`
  padding-top: 6px;
  margin-top: 4px;
  border-top: 1px solid var(--bg-300);
`;

const CheckoutButton = styled.button`
  text-transform: uppercase;
  width: 100%;
  margin-top: var(--spacing-xs);
  font-weight: 600;
  letter-spacing: 0.03em;
`;

const Notice = styled.p`
  width: 96%;
  margin: 8px 0 16px;
  font-style: italic;
  text-align: center;
  color: var(--text-200);
  font-size: var(--font-size-small);
`;

const EmptyState = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-sm);
  padding: var(--spacing-xl) var(--spacing-lg);
  text-align: center;
`;

const EmptyIconWrap = styled.div`
  width: 84px;
  height: 84px;
  border-radius: 50%;
  background-color: var(--bg-200);
  display: flex;
  align-items: center;
  justify-content: center;
`;

const EmptyTitle = styled.h3`
  color: var(--text-100);
  font-weight: 600;
`;

const EmptySubtitle = styled.p`
  color: var(--text-200);
  font-size: var(--font-size-small);
  max-width: 240px;
`;

const ContinueButton = styled.button`
  margin-top: var(--spacing-xs);
`;

const CartMenu = () => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [shouldBeVisible, setShouldBeVisible] = useState(false);
  const { currencyTag, shippingMethod } = useContext(ProductContext);
  const navigate = useNavigate();
  const { goToCheckout, goToShop } = useNavigation();

  useEffect(() => {
    if (isOpen) {
      setShouldBeVisible(true);
      document.body.style.overflow = "hidden";
    } else {
      const timer = setTimeout(() => {
        setShouldBeVisible(false);
      }, 300);
      document.body.style.overflow = "";
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  const cartItems = useSelector((state) => state.cart.itemsList);
  let totalQuantity = cartItems.reduce(
    (total, item) => total + item.quantity,
    0
  );
  const subtotal = cartItems.reduce(
    (total, item) => total + item.product.discount_price * item.quantity,
    0
  );
  const subtotalRounded = parseFloat(subtotal.toFixed(2));
  const savings = cartItems.reduce(
    (totalSavings, item) =>
      totalSavings +
      (item.product.price - item.product.discount_price) * item.quantity,
    0
  );
  const savingsRounded = parseFloat(savings.toFixed(2));
  const [shippingCost, setShippingCost] = useState(0.0);
  const currency = localStorage.getItem("currency") || DEFAULT_CURRENCY;
  useEffect(() => {
    if (qualifiesForFreeShipping(subtotalRounded, shippingMethod.name)) {
      setShippingCost(0);
    } else {
      setShippingCost(
        getShippingCostPrice(shippingMethod.name, currency, DEFAULT_CURRENCY)
      );
    }
  }, [subtotalRounded, shippingMethod]);

  const grandTotal = subtotalRounded + shippingCost;
  const grandTotalRounded = parseFloat(grandTotal.toFixed(2));

  const inStockItems = cartItems.filter(
    (item) => item.product.is_in_stock === "in_stock"
  );
  const otherItems = cartItems.filter(
    (item) => item.product.is_in_stock !== "in_stock"
  );

  const remainingForFreeShipping = parseFloat(
    Math.max(freeShippingThreshold - subtotalRounded, 0).toFixed(2)
  );
  const freeShippingUnlocked = remainingForFreeShipping === 0;
  const shippingProgressPercent = Math.min(
    (subtotalRounded / freeShippingThreshold) * 100,
    100
  );

  const handleContinueShopping = () => {
    setIsOpen(false);
    navigate(goToShop());
  };

  return (
    <ContainerX>
      <CartContainer
        onClick={toggleMenu}
        role="button"
        tabIndex={0}
        aria-label={t("ARIA.OPEN_CART")}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            toggleMenu();
          }
        }}
      >
        <svg
          fill="var(--text-100)"
          width="22px"
          height="22px"
          viewBox="0 0 32 32"
          id="Outlined"
          xmlns="http://www.w3.org/2000/svg"
        >
          <title />

          <g id="Fill">
            <path d="M23.78,11,22.35,5.27A3,3,0,0,0,19.44,3H12.56A3,3,0,0,0,9.65,5.27L8.22,11H2.16L6.09,26.73A3,3,0,0,0,9,29H23a3,3,0,0,0,2.91-2.27L29.84,11ZM15,21v6H12.83L11.7,21Zm-3.67-2L10.2,13H15v6ZM17,21h3.3l-1.13,6H17Zm0-2V13h4.8l-1.13,6Zm6.83-6h3.45l-1.5,6H22.7ZM11.59,5.76a1,1,0,0,1,1-.76h6.88a1,1,0,0,1,1,.76L21.72,11H10.28ZM8.17,13,9.3,19H6.22l-1.5-6ZM8,26.24,6.72,21h3l1.12,6H9A1,1,0,0,1,8,26.24Zm15.94,0A1,1,0,0,1,23,27H21.21l1.12-6h3Z" />
          </g>
        </svg>
        {totalQuantity !== 0 && <SumQuantity>{totalQuantity}</SumQuantity>}
        <CartText>{t("CART.TITLE")}</CartText>
      </CartContainer>

      <Container
        onClick={toggleMenu}
        $isOpen={isOpen}
        $shouldBeVisible={shouldBeVisible}
      />
      <Wrapper
        $isOpen={isOpen}
        $shouldBeVisible={shouldBeVisible}
        onClick={(event) => {
          event.stopPropagation();
        }}
      >
        <XDiv>
          <Logo>
            {totalQuantity} {t("CART.ITEMS")}
          </Logo>
          <IconDiv>
            <CloseButton
              onClick={toggleMenu}
              role="button"
              tabIndex={0}
              aria-label={t("ARIA.CLOSE_CART")}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  toggleMenu();
                }
              }}
            >
              <svg width="18px" height="18px" viewBox="0 0 1024 1024" xmlns="http://www.w3.org/2000/svg">
                <path
                  fill="var(--text-200)"
                  d="M195.2 195.2a64 64 0 0 1 90.496 0L512 421.504 738.304 195.2a64 64 0 0 1 90.496 90.496L602.496 512 828.8 738.304a64 64 0 0 1-90.496 90.496L512 602.496 285.696 828.8a64 64 0 0 1-90.496-90.496L421.504 512 195.2 285.696a64 64 0 0 1 0-90.496z"
                />
              </svg>
            </CloseButton>
          </IconDiv>
        </XDiv>

        {totalQuantity > 0 && (
          <ShippingBar>
            <ShippingText>
              {freeShippingUnlocked
                ? `🎉 ${t("CART.FREE_SHIPPING_UNLOCKED")}`
                : t("CART.FREE_SHIPPING_PROGRESS", {
                    amount: `${currencyTag}${remainingForFreeShipping.toFixed(2)}`,
                  })}
            </ShippingText>
            <ProgressTrack>
              <ProgressFill $percent={shippingProgressPercent} />
            </ProgressTrack>
          </ShippingBar>
        )}

        {totalQuantity === 0 ? (
          <EmptyState>
            <EmptyIconWrap>
              <svg
                width="38px"
                height="38px"
                viewBox="0 0 32 32"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fill="var(--bg-300)"
                  d="M23.78,11,22.35,5.27A3,3,0,0,0,19.44,3H12.56A3,3,0,0,0,9.65,5.27L8.22,11H2.16L6.09,26.73A3,3,0,0,0,9,29H23a3,3,0,0,0,2.91-2.27L29.84,11ZM15,21v6H12.83L11.7,21Zm-3.67-2L10.2,13H15v6ZM17,21h3.3l-1.13,6H17Zm0-2V13h4.8l-1.13,6Zm6.83-6h3.45l-1.5,6H22.7ZM11.59,5.76a1,1,0,0,1,1-.76h6.88a1,1,0,0,1,1,.76L21.72,11H10.28ZM8.17,13,9.3,19H6.22l-1.5-6ZM8,26.24,6.72,21h3l1.12,6H9A1,1,0,0,1,8,26.24Zm15.94,0A1,1,0,0,1,23,27H21.21l1.12-6h3Z"
                />
              </svg>
            </EmptyIconWrap>
            <EmptyTitle>{t("CART.EMPTY_TITLE")}</EmptyTitle>
            <EmptySubtitle>{t("CART.EMPTY_SUBTITLE")}</EmptySubtitle>
            <ContinueButton onClick={handleContinueShopping}>
              {t("CART.CONTINUE_SHOPPING")}
            </ContinueButton>
          </EmptyState>
        ) : (
          <MiddleDiv>
            <ItemsInner>
              {inStockItems.map((item, index) => (
                <CartProduct item={item} key={index} />
              ))}
              {otherItems.length > 0 && (
                <>
                  <Notice>{t("CART.NOTICE")}</Notice>
                  {otherItems.map((item, idx) => (
                    <CartProduct item={item} key={`out-${idx}`} />
                  ))}
                </>
              )}
            </ItemsInner>
          </MiddleDiv>
        )}

        {totalQuantity > 0 && (
          <Bottom>
            <BottomWrapper>
              {savingsRounded > 0 && (
                <SavingsRow>
                  <span>{t("CART.SAVINGS")}</span>
                  <span>
                    -{currencyTag}
                    {savingsRounded.toFixed(2)}
                  </span>
                </SavingsRow>
              )}
              <SummaryRow>
                <span>{t("CART.SHOPPING_BASKET")}</span>
                <span>
                  {currencyTag}
                  {subtotalRounded.toFixed(2)}
                </span>
              </SummaryRow>
              <TotalRow>
                <h3>{t("CART.CHECKOUT")}</h3>
                <h3>
                  {currencyTag}
                  {grandTotalRounded.toFixed(2)}
                </h3>
              </TotalRow>
              <CheckoutButton
                onClick={() => navigate(goToCheckout())}
                disabled={totalQuantity == 0}
              >
                {t("CART.CHECKOUT")}
              </CheckoutButton>
            </BottomWrapper>
          </Bottom>
        )}
      </Wrapper>
    </ContainerX>
  );
};

export default CartMenu;
